/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential.
 *
 * THE FIRM, ITS REGIME AND WHO DECIDES, FOR EVERY MODEL PROMPT (v1_970).
 * Ask Alex, asked on production to draft a decline for Sage Capital Management
 * AG (a Swiss VQF SRO member), grounded it in FCA SYSC and JMLSG and wrote
 * "CompliantID's due diligence obligations". Its prompt said it served the
 * MLRO "at CompliantID, a regulated digital asset compliance firm" and was "an
 * expert in FCA rules", and it was given no firm name or regime at all.
 *
 * firmRegime(firm) reads what the firm's configuration already holds: its name
 * (the brand name the firm set, else firms.name), the regulators the operator
 * ticked (compliance_config.regulators, with otherRegulator), its AML
 * supervisor (firms.supervisor) and a regime stored in risk_config. Nothing is
 * defaulted: with none of these set, the regime is reported as not set.
 * firmRegimeBlock(firm) is the text every officer facing (and client facing)
 * prompt carries: the firm is the obliged entity and decision maker,
 * CompliantID is only the software, cite only the firm's own regime (one per
 * entity when there are several), and with no regime set, say so and ask.
 *
 * v1_975: WHICH ENTITY HOLDS THE CLIENT. For a firm with more than one
 * regulated entity (Edgewater: Mauritius FSC, UK FCA, Canada FINTRAC), the
 * case records the holding entity as one of the firm's regulator codes
 * (onboarding_sessions.firm_entity, such as 'mu_fsc'). firmEntities(firm) lists
 * the entities an officer may pick, checkFirmEntity(firm, code) is the one rule
 * the invite and the case save both apply, and firmRegimeBlock(firm, code)
 * tells the model to use only that entity's regime. A single regime firm has
 * no entity to record and its block is unchanged.
 * Pure: no network, no React.
 */
import { regimeFromSupervisor, CITEABLE_REGIMES } from './supervisor-regime.js';
import { citableRegimeId, REGIME_UNCITED } from './regimes.js';

// The ticked regulators (src/MLRODashboard.jsx REGULATOR_OPTIONS), as the
// authority and the law a model may cite for them.
export const REGULATOR_REGIMES = {
  fca_uk: { entity: 'United Kingdom', authority: 'the FCA', law: 'the Money Laundering Regulations 2017 and JMLSG guidance' },
  ch_vqf_finma: { entity: 'Switzerland', authority: 'its Swiss self regulatory organisation (such as VQF) under FINMA oversight', law: 'the Swiss Anti Money Laundering Act (AMLA, GwG), the AMLO and the SRO rules' },
  eu_amld: { entity: 'European Union', authority: 'its national competent authority', law: 'the EU Anti Money Laundering Directives as transposed nationally' },
  de_bafin: { entity: 'Germany', authority: 'BaFin', law: 'the German Money Laundering Act (GwG)' },
  sg_mas: { entity: 'Singapore', authority: 'the MAS', law: 'the CDSA and the applicable MAS Notices' },
  us_fincen: { entity: 'United States', authority: 'FinCEN (and OFAC for sanctions)', law: 'the Bank Secrecy Act and FinCEN rules' },
  ae_vara: { entity: 'United Arab Emirates', authority: 'VARA, the DFSA or the FSRA', law: 'UAE Federal Decree Law 20 of 2018 and the authority\'s rules' },
  mu_fsc: { entity: 'Mauritius', authority: 'the FSC (with the FIU for reporting)', law: 'FIAMLA 2002, the FIAML Regulations 2018 and the FSC AML/CFT Handbook' },
  ca_fintrac: { entity: 'Canada', authority: 'FINTRAC', law: 'the PCMLTFA and PCMLTFR' },
  // v1_978 part B: the law named here is what src/shared/regimes.js GI cites,
  // each section read in the official text.
  gi_gfsc: { entity: 'Gibraltar', authority: 'the Gibraltar Financial Services Commission (GFSC), with the GFIU for reporting', law: 'the Proceeds of Crime Act 2015 (Gibraltar), the Sanctions Act 2019 and the GFSC AML/CFT/CPF Guidance Notes' },
};
// The plain label an officer picks the holding entity by (v1_975).
export const ENTITY_LABELS = {
  fca_uk: 'United Kingdom (FCA)',
  ch_vqf_finma: 'Switzerland (VQF, FINMA)',
  eu_amld: 'European Union (AMLD)',
  de_bafin: 'Germany (BaFin)',
  sg_mas: 'Singapore (MAS)',
  us_fincen: 'United States (FinCEN)',
  ae_vara: 'United Arab Emirates (VARA, DFSA)',
  mu_fsc: 'Mauritius (FSC)',
  ca_fintrac: 'Canada (FINTRAC)',
};
// The shape the database check also enforces (onboarding_sessions.firm_entity).
export const FIRM_ENTITY_CODE = /^[a-z][a-z0-9_]{1,31}$/;
const CITEABLE_LAW = Object.fromEntries(CITEABLE_REGIMES.map(r => [r.id, r]));

export function firmName(firm) {
  const b = (firm && firm.branding) || {};
  return String((b.legalName || b.displayName || (firm && firm.name) || '')).trim() || null;
}

// { name, regimes: [{ entity, authority, law, source }], set, multi }
export function firmRegime(firm) {
  const name = firmName(firm);
  const regimes = [];
  const cfg = (firm && firm.compliance_config) || {};
  for (const code of (Array.isArray(cfg.regulators) ? cfg.regulators : [])) {
    if (REGULATOR_REGIMES[code]) regimes.push({ ...REGULATOR_REGIMES[code], code, label: ENTITY_LABELS[code], source: 'regulators ticked for the firm' });
    else if (code === 'other' && cfg.otherRegulator && String(cfg.otherRegulator).trim()) regimes.push({ entity: String(cfg.otherRegulator).trim(), authority: String(cfg.otherRegulator).trim(), law: 'the AML law and rules of that framework', code, label: String(cfg.otherRegulator).trim(), source: 'regulator named for the firm' });
  }
  if (!regimes.length) {
    const sup = firm && firm.supervisor ? String(firm.supervisor).trim() : '';
    const { regime } = regimeFromSupervisor(sup);
    const stored = citableRegimeId(firm && firm.risk_config && firm.risk_config.regime);
    const id = regime || (stored && CITEABLE_LAW[stored] ? stored : null);
    if (id && CITEABLE_LAW[id]) regimes.push({ entity: CITEABLE_LAW[id].label, authority: sup || CITEABLE_LAW[id].label + ' supervisor', law: CITEABLE_LAW[id].statute, source: sup ? 'the firm\'s AML supervisor' : 'the firm\'s regime setting' });
    else if (sup) regimes.push({ entity: sup, authority: sup, law: 'the AML law and rules this supervisor applies', source: 'the firm\'s AML supervisor' });
  }
  return { name, regimes, set: regimes.length > 0, multi: regimes.length > 1 };
}

// THE REGIME THE RISK ENGINE CITES (v1_972). "Set" means exactly what
// firmRegime means by it, so a prompt and the risk assessment never disagree
// about whether the firm has a regime. The id is the citeable regime: the one
// stored in risk_config (the existing choice, kept first so no set firm's
// citations move), else the one its supervisor implies, else the one its ticked
// regulators all imply. A regime that is set but not citeable (a Singapore
// supervisor, or regulators in two regimes) is REGIME_UNCITED: no statute is
// cited and the file says the platform holds none for it. Nothing set: null.
const REGULATOR_REGIME_ID = { fca_uk: 'UK', ch_vqf_finma: 'CH', eu_amld: 'EU', de_bafin: 'EU', mu_fsc: 'MU', ca_fintrac: 'CA' };
export function firmCitationRegime(firm) {
  const fr = firmRegime(firm);
  if (!fr.set) return { id: null, set: false, name: null };
  let id = citableRegimeId(firm && firm.risk_config && firm.risk_config.regime)
    || citableRegimeId(regimeFromSupervisor(firm && firm.supervisor).regime);
  if (!id) {
    const cfg = (firm && firm.compliance_config) || {};
    const codes = (Array.isArray(cfg.regulators) ? cfg.regulators : []).filter(c => REGULATOR_REGIMES[c] || c === 'other');
    const ids = new Set(codes.map(c => REGULATOR_REGIME_ID[c] || null));
    if (ids.size === 1 && !ids.has(null)) id = [...ids][0];
  }
  return { id: id || REGIME_UNCITED, set: true, name: id ? null : (fr.regimes.map(g => g.entity).join(', ') || null) };
}

// The firm's risk_config as the risk engine is given it: the stored
// configuration with the regime resolved by firmCitationRegime. Null when the
// firm has neither (the engine then reports the regime as not set).
// v1_972b: for a firm with more than one regulated entity, the entity recorded
// as holding the case's client (onboarding_sessions.firm_entity, v1_975)
// decides the regime, ahead of anything set for the firm as a whole, so the
// risk assessment cites the same regime the prompts do for that client. An
// entity whose regime the platform holds no citations for cites no statute and
// names the entity. A single regime firm records no entity and is unchanged;
// an entity code that is not one of the firm's own is ignored.
export function riskConfigForFirm(firm, entityCode = null) {
  const rc = (firm && firm.risk_config && typeof firm.risk_config === 'object') ? firm.risk_config : null;
  const ent = entityCode ? firmEntities(firm) : null;
  if (ent && ent.multi && ent.list.some(e => e.code === entityCode)) {
    const id = REGULATOR_REGIME_ID[entityCode] || null;
    return { ...(rc || {}), regime: id || REGIME_UNCITED, regimeEntity: entityCode, ...(id ? {} : { regimeName: firmEntityLabel(firm, entityCode) }) };
  }
  const r = firmCitationRegime(firm);
  if (!rc && !r.set) return null;
  return { ...(rc || {}), regime: r.id, ...(r.name ? { regimeName: r.name } : {}) };
}

// The entities an officer may record as holding a client: one per regulator
// ticked for the firm, [{ code, label }]. Only a firm with more than one has a
// choice to make (multi); a single regime firm records none.
export function firmEntities(firm) {
  const list = firmRegime(firm).regimes.filter(g => g.code).map(g => ({ code: g.code, label: g.label }));
  return { list, multi: list.length > 1 };
}

// The one rule for recording the holding entity (invite and case save):
// required for a multi entity firm and one of its own regulators; never
// recorded for a single regime firm. { ok, code, error }.
export function checkFirmEntity(firm, raw) {
  const code = raw === undefined || raw === null ? '' : String(raw).trim();
  const { list, multi } = firmEntities(firm);
  const names = list.map(e => e.label).join(', ');
  if (!multi) {
    if (!code) return { ok: true, code: null };
    return { ok: false, code: null, error: 'This firm has one regulatory regime, so no holding entity is recorded for its clients.' };
  }
  if (!code) return { ok: false, code: null, error: `This firm has more than one regulated entity (${names}). Choose the entity that holds this client.` };
  if (!FIRM_ENTITY_CODE.test(code) || !list.some(e => e.code === code)) return { ok: false, code: null, error: `That entity is not one of this firm's regulated entities. Choose one of: ${names}.` };
  return { ok: true, code };
}

export function firmEntityLabel(firm, code) {
  if (!code) return null;
  const e = firmEntities(firm).list.find(x => x.code === code);
  return e ? e.label : (ENTITY_LABELS[code] || String(code));
}

export function firmRegimeBlock(firm, entityCode = null) {
  const r = firmRegime(firm);
  const who = r.name || 'the firm using this platform';
  const lines = [
    `THE FIRM: ${who}. ${who} is the obliged entity: it onboards this client, carries the anti money laundering and due diligence obligations, holds the client relationship and makes and records every decision. The officers using this tool work for ${who}.`,
    `CompliantID is only the name of the software ${who} uses. Never write that CompliantID has obligations, carries out due diligence, makes, records or communicates a decision, onboards the client or holds the relationship. Write "${r.name || 'the firm'}" or "the firm" instead.`,
  ];
  if (!r.set) {
    lines.push(`THE FIRM'S AML REGIME IS NOT SET in its configuration on CompliantID. Do not cite any country's anti money laundering law, regulator or guidance, and never assume the United Kingdom, the FCA, SYSC, JMLSG, the MLR 2017 or POCA. Say plainly that the firm's regime is not set, ask the officer which regime applies, and until then refer only to FATF standards in general terms.`);
  } else if (!r.multi) {
    const g = r.regimes[0];
    lines.push(`THE FIRM'S AML REGIME (as set for this firm): ${g.entity}, supervised by ${g.authority}, under ${g.law}. Cite only this regime's laws, rules, guidance and authorities. Never cite another country's regime (for example the FCA, SYSC, JMLSG, the MLR 2017 or POCA for a firm that is not UK regulated) unless the officer asks about it by name. FATF standards may be cited as international standards, alongside the firm's regime, never instead of it.`);
  } else if (entityCode && r.regimes.some(g => g.code === entityCode)) {
    // v1_975: the case records the entity that holds this client.
    const g = r.regimes.find(x => x.code === entityCode);
    lines.push(`THE FIRM'S AML REGIME FOR THIS CLIENT: This client is held by the ${g.label} entity: use only ${g.entity}, supervised by ${g.authority}, under ${g.law}. Cite only this regime's laws, rules, guidance and authorities for this client. Never cite the regime of another entity of the firm, or of any other country, unless the officer asks about it by name. FATF standards may be cited as international standards, alongside this regime, never instead of it.`);
  } else {
    lines.push(`THE FIRM'S AML REGIMES (as set for this firm, one per regulated entity or licence):`);
    r.regimes.forEach((g, i) => lines.push(`${i + 1}. ${g.entity}: supervised by ${g.authority}, under ${g.law}.`));
    if (entityCode) lines.push(`The case records a holding entity (${String(entityCode).slice(0, 32)}) that is not among the firm's regulated entities, so treat the holding entity as not recorded.`);
    lines.push(`Use only the regime of the entity that holds this client relationship. If the file does not say which entity that is, say so and ask the officer before citing any regime. Never cite a regime that is not in this list (for example the FCA for an entity that is not UK regulated). FATF standards may be cited as international standards, alongside the entity's regime, never instead of it.`);
  }
  lines.push('Write without dashes: no em dashes, no en dashes and no hyphens between words; use commas, full stops or parentheses.');
  return lines.join('\n');
}

// The client portal's Alex: who it speaks for, and that CompliantID is only the software.
export function firmIdentityBlockForClient(firm) {
  const n = firmName(firm);
  return `WHO YOU ACT FOR: ${n || 'the firm onboarding this client'}. ${n || 'That firm'} is the firm onboarding this client and the one that reviews and decides on the file. Wherever these instructions say you work "at CompliantID" or for CompliantID, read it as ${n || 'that firm'}: CompliantID is only the name of the software. Never tell the client that CompliantID onboards them, reviews or decides on their file, or holds their relationship.`;
}
